import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import adminService, { AdminCaseListItem } from '../../services/admin.service';

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const getStatusBadge = (value: string): string => {
  switch (value) {
    case 'completed':
      return 'status-badge bg-emerald-100 text-emerald-800';
    case 'in_review':
      return 'status-badge bg-amber-100 text-amber-800';
    case 'submitted':
    case 'paid':
      return 'status-badge bg-blue-100 text-blue-800';
    default:
      return 'status-badge bg-gray-100 text-gray-700';
  }
};

const AdminCasesPage: React.FC = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState<AdminCaseListItem[]>([]);
  const [loading, setLoading] = useState(true);

  const loadCases = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getCases();
      setCases(data);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load cases';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCases();
  }, [loadCases]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading admin cases" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#C6A87C]">Internal Admin</p>
            <h1 className="text-3xl font-bold text-gray-900">Submitted cases</h1>
            <p className="mt-2 text-gray-600">Newest review requests first. Open a case to inspect evidence, answers, and letter output.</p>
          </div>
          <button type="button" onClick={() => void loadCases()} className="btn-primary px-4 py-2 text-sm">
            Refresh
          </button>
        </div>

        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">Total cases</p>
            <p className="mt-2 text-2xl font-bold text-gray-900">{cases.length}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">In review</p>
            <p className="mt-2 text-2xl font-bold text-amber-700">{cases.filter((item) => item.caseStatus === 'in_review').length}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">Completed</p>
            <p className="mt-2 text-2xl font-bold text-emerald-700">{cases.filter((item) => item.caseStatus === 'completed').length}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">Unknown payment</p>
            <p className="mt-2 text-2xl font-bold text-slate-700">{cases.filter((item) => item.paymentStatus === 'unknown').length}</p>
          </div>
        </div>

        {cases.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-12 text-center shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">No submitted cases</h2>
            <p className="mt-2 text-gray-600">Human review requests will appear here after users submit them.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Case ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">User email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Plan</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Payment</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Case status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {cases.map((item) => (
                    <tr
                      key={item.requestId}
                      className="cursor-pointer hover:bg-amber-50/40"
                      onClick={() => navigate(`/admin/cases/${item.caseId}`)}
                    >
                      <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-gray-900">
                        <Link
                          to={`/admin/cases/${item.caseId}`}
                          className="text-[#8E6D3B] underline-offset-2 hover:underline"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {item.caseId}
                        </Link>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">{item.userEmail || '—'}</td>
                      <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-700">{formatDate(item.createdAt)}</td>
                      <td className="px-4 py-4 text-sm text-gray-700">{item.planType || '—'}</td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        <span className={getStatusBadge(item.paymentStatus)}>{item.paymentStatus || 'unknown'}</span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        <span className={getStatusBadge(item.caseStatus)}>{item.caseStatus || 'unknown'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCasesPage;
